/**
 * Kling Image O3 on fal.ai (Phase 1, U8): kling-image-o3.
 *
 * Replaces: falai.ts async image arm `case 'kling-image-o3'`, the Kling branch of
 * ProviderService.generateImageFalAI, falModels.buildKlingImageInput / ensureKlingImageReferences
 * as the payload source, and CreditService's `modelId === 'kling-image-o3'` price branch
 * (KLING_IMAGE_COSTS_USD: ceil(usd x 450) x ceil(units)).
 *
 * PURE: `import type` + sibling modules only (purity.test). The @ImageN prompt prefix and the
 * reference-dependent aspect fold run in the server-only payload hook `klingImageRefPrefix`
 * (services/generation/hooks/klingImageRefPrefix.ts, R9).
 *
 * Unlike every other fal image model, Kling's input is built wholesale (no image_size, no
 * enable_safety_checker): { prompt, aspect_ratio, resolution, output_format, num_images: 1,
 * result_type: 'single', image_urls? (<= 10) }, identically on both surfaces.
 */
import type { ModelDescriptor } from '../types';

export const KLING_IMAGE_O3: ModelDescriptor = {
  id: 'kling-image-o3', provider: 'falai', face: 'image', displayName: 'Kling Image O3', ledgerName: 'Kling Image', status: 'live',
  prompt: { field: 'prompt', required: false },
  modes: {
    t2i: { endpoint: 'fal-ai/kling-image/o3/text-to-image', ports: [], media: {} },
    // min 0: a maskUrl-only editor request picks this endpoint with no image_urls (legacy).
    edit: { endpoint: 'fal-ai/kling-image/o3/image-to-image',
            ports: [{ slot: 'refs', kind: 'image', min: 0, max: 10, handle: 'input-image' }],
            media: { refs: { field: 'image_urls', many: true, max: 10, encode: 'image' } } },
  },
  fixed: { num_images: 1, result_type: 'single' },
  params: [
    // buildKlingImageInput: an allowed ratio passes; anything else is 'auto' WITH references and
    // '16:9' without. 'auto' is the coercion default; the t2i constraint folds it to '16:9'
    // (t2i has no 'auto'), and the hook folds the edit-without-references case.
    { id: 'aspectRatio', type: 'enum', values: ['16:9', '9:16', '1:1', '4:3', '3:4', '3:2', '2:3', '21:9', 'auto'], default: 'auto',
      invalid: { coerce: { kind: 'default' } }, wire: { field: 'aspect_ratio' }, ui: { label: 'Aspect ratio', tier: 'basic' } },
    { id: 'resolution', type: 'enum', values: ['1K', '2K', '4K'], default: '1K', role: 'tier',
      invalid: { coerce: { kind: 'default' } }, wire: { field: 'resolution' }, ui: { label: 'Resolution', tier: 'basic' } },
    { id: 'outputFormat', type: 'enum', values: ['png', 'jpeg', 'webp'], default: 'png',
      invalid: { coerce: { kind: 'default' } }, wire: { field: 'output_format' }, ui: { label: 'Format', tier: 'advanced' } },
  ],
  constraints: [{
    id: 'kling-image-t2i-no-auto', when: { $mode: { eq: 't2i' }, aspectRatio: { eq: 'auto' } }, force: { aspectRatio: '16:9' },
    why: "'auto' exists only in the image-to-image enum; buildKlingImageInput folds it to the t2i default",
    source: 'fal OpenAPI KlingImageO3TextToImageInput.aspect_ratio (2026-09-19); falModels.buildKlingImageInput',
  }],
  hooks: { payload: 'klingImageRefPrefix' },
  pricing: {
    unit: 'generation', units: { fixed: 1 }, rounding: 'table-min1', policy: 'fal-image-35',
    skus: [{
      id: 'kling-image-o3',
      rate: { by: ['resolution'], table: { '1K': 13, '2K': 13, '4K': 26 }, fallbackKey: '1K' },
      vendor: { usd: { '1K': 0.028, '2K': 0.028, '4K': 0.056 }, per: 'image', field: 'pricingInfoOverride',
        source: 'fal-ai/kling-image/o3/text-to-image and /image-to-image',
        text: 'Your request will cost **$0.028** per image for **1K/2K**, price will be double for **4K**', verified: '2026-09-19' },
      registry: { displayName: 'Kling Image', operationType: 'image', publishedTier: '1K', flatRate: false },
    }],
  },
  knowledge: {
    summary: 'Up to 10 reference images, addressed as @Image1..@Image10 in the prompt; 1K and 2K cost the same, 4K doubles.',
  },
  schemaVerified: '2026-09-19',
};

export const KLING_IMAGE_MODELS: readonly ModelDescriptor[] = [KLING_IMAGE_O3];
